import * as THREE from 'three';

/**
 * One dynamic paint texture bound to one paintable mesh.
 *
 * How stamping works (the slope-correct projection trick):
 * to apply a stamp we re-render the paintable mesh itself into the paint
 * render target, but the vertex shader outputs each vertex's UV as its
 * clip-space position while passing the true world position to the fragment
 * shader. The fragment shader computes 3D world-space distance from the
 * fragment to the spray hit point and applies radial falloff on that
 * distance. The mark is therefore a genuine circle on any incline or curved
 * surface, with zero UV stretching artifacts.
 *
 * The target stores PREMULTIPLIED alpha. That makes both stamping ("over"
 * compositing) and snowfall fade (multiply everything by 1-decay) single
 * blended draws into one render target — no ping-pong copy needed — and hue
 * stays constant while paint fades under fresh snow.
 */

const MAX_STAMPS_PER_DRAW = 32;

const stampVertexShader = /* glsl */ `
  varying vec3 vWorldPos;
  void main() {
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
  }
`;

const stampFragmentShader = /* glsl */ `
  uniform vec3 uPos[${MAX_STAMPS_PER_DRAW}];
  uniform vec3 uColor[${MAX_STAMPS_PER_DRAW}];
  uniform float uRadius[${MAX_STAMPS_PER_DRAW}];
  uniform float uAlpha[${MAX_STAMPS_PER_DRAW}];
  uniform int uCount;
  varying vec3 vWorldPos;

  void main() {
    vec3 premul = vec3(0.0);
    float a = 0.0;
    for (int i = 0; i < ${MAX_STAMPS_PER_DRAW}; i++) {
      if (i >= uCount) break;
      float x = distance(vWorldPos, uPos[i]) / uRadius[i];
      if (x >= 1.0) continue;
      // Soft airbrush profile: dense gaussian core, feathered edge, zero at rim.
      float f = exp(-x * x * 4.0) * (1.0 - smoothstep(0.72, 1.0, x));
      float ai = f * uAlpha[i];
      premul = uColor[i] * ai + premul * (1.0 - ai);
      a = ai + a * (1.0 - ai);
    }
    if (a < 0.004) discard;
    gl_FragColor = vec4(premul, a);
  }
`;

const fadeVertexShader = /* glsl */ `
  void main() {
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fadeFragmentShader = /* glsl */ `
  uniform float uDecay;
  void main() {
    gl_FragColor = vec4(0.0, 0.0, 0.0, uDecay);
  }
`;

/** Premultiplied "over" blending: dst' = src + dst * (1 - src.a). */
function configurePremultipliedOver(mat: THREE.ShaderMaterial): void {
  mat.transparent = true;
  mat.blending = THREE.CustomBlending;
  mat.blendSrc = THREE.OneFactor;
  mat.blendDst = THREE.OneMinusSrcAlphaFactor;
  mat.blendSrcAlpha = THREE.OneFactor;
  mat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  mat.depthTest = false;
  mat.depthWrite = false;
}

export interface PendingStamp {
  pos: THREE.Vector3;
  color: THREE.Color;
  radius: number;
  alpha: number;
}

const dummyCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
const fadeGeometry = new THREE.BufferGeometry();
fadeGeometry.setAttribute(
  'position',
  new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3),
);

export class PaintSurface {
  readonly id: string;
  readonly mesh: THREE.Mesh;
  /** Snow materials sample this; it always points at the live paint texture. */
  readonly paintUniform: { value: THREE.Texture };

  private target: THREE.WebGLRenderTarget;
  private stampScene: THREE.Scene;
  private stampMaterial: THREE.ShaderMaterial;
  private stampMesh: THREE.Mesh;
  private fadeScene: THREE.Scene;
  private fadeMaterial: THREE.ShaderMaterial;
  private pending: PendingStamp[] = [];
  private needsClear = true;

  constructor(
    id: string,
    mesh: THREE.Mesh,
    textureSize: number,
    uniformRef?: { value: THREE.Texture },
  ) {
    this.id = id;
    this.mesh = mesh;
    this.target = this.createTarget(textureSize);
    // Adopt the uniform the mesh's snow material already samples, so the
    // material starts showing paint the moment this surface goes live.
    this.paintUniform = uniformRef ?? { value: this.target.texture };
    this.paintUniform.value = this.target.texture;

    this.stampMaterial = new THREE.ShaderMaterial({
      vertexShader: stampVertexShader,
      fragmentShader: stampFragmentShader,
      uniforms: {
        uPos: { value: Array.from({ length: MAX_STAMPS_PER_DRAW }, () => new THREE.Vector3()) },
        uColor: { value: Array.from({ length: MAX_STAMPS_PER_DRAW }, () => new THREE.Color()) },
        uRadius: { value: new Array(MAX_STAMPS_PER_DRAW).fill(1) as number[] },
        uAlpha: { value: new Array(MAX_STAMPS_PER_DRAW).fill(0) as number[] },
        uCount: { value: 0 },
      },
      side: THREE.DoubleSide,
    });
    configurePremultipliedOver(this.stampMaterial);

    // Render the paintable mesh's own geometry in UV space, carrying its world matrix.
    this.stampMesh = new THREE.Mesh(mesh.geometry, this.stampMaterial);
    this.stampMesh.matrixAutoUpdate = false;
    this.stampMesh.frustumCulled = false;
    this.stampScene = new THREE.Scene();
    this.stampScene.add(this.stampMesh);

    this.fadeMaterial = new THREE.ShaderMaterial({
      vertexShader: fadeVertexShader,
      fragmentShader: fadeFragmentShader,
      uniforms: { uDecay: { value: 0 } },
    });
    configurePremultipliedOver(this.fadeMaterial);
    const fadeMesh = new THREE.Mesh(fadeGeometry, this.fadeMaterial);
    fadeMesh.frustumCulled = false;
    this.fadeScene = new THREE.Scene();
    this.fadeScene.add(fadeMesh);
  }

  private createTarget(size: number): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(size, size, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
    });
    return target;
  }

  enqueue(stamp: PendingStamp): void {
    this.pending.push(stamp);
  }

  get hasPending(): boolean {
    return this.pending.length > 0 || this.needsClear;
  }

  /** Flush pending stamps into the render target. Call once per frame. */
  flush(renderer: THREE.WebGLRenderer): void {
    if (!this.hasPending) return;
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(this.target);

    if (this.needsClear) {
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, false, false);
      this.needsClear = false;
    }

    if (this.pending.length > 0) {
      this.stampMesh.matrix.copy(this.mesh.matrixWorld);
      this.stampMesh.matrixWorld.copy(this.mesh.matrixWorld);
      const u = this.stampMaterial.uniforms;
      const posArr = u.uPos!.value as THREE.Vector3[];
      const colArr = u.uColor!.value as THREE.Color[];
      const radArr = u.uRadius!.value as number[];
      const alphaArr = u.uAlpha!.value as number[];
      for (let offset = 0; offset < this.pending.length; offset += MAX_STAMPS_PER_DRAW) {
        const count = Math.min(MAX_STAMPS_PER_DRAW, this.pending.length - offset);
        for (let i = 0; i < count; i++) {
          const s = this.pending[offset + i]!;
          posArr[i]!.copy(s.pos);
          colArr[i]!.copy(s.color);
          radArr[i] = s.radius;
          alphaArr[i] = s.alpha;
        }
        u.uCount!.value = count;
        renderer.render(this.stampScene, dummyCamera);
      }
      this.pending.length = 0;
    }

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
  }

  /** Snow-accumulation pass: everything already painted fades toward fresh snow. */
  fade(renderer: THREE.WebGLRenderer, decay: number): void {
    if (decay <= 0 || this.needsClear) return;
    this.fadeMaterial.uniforms.uDecay!.value = Math.min(decay, 1);
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(this.target);
    renderer.render(this.fadeScene, dummyCamera);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
  }

  /** Instantly restore fresh snow. */
  clear(): void {
    this.pending.length = 0;
    this.needsClear = true;
  }

  /** Recreate the render target at a new resolution (quality change). The
   * caller is expected to replay the stroke log afterwards. */
  resize(size: number): void {
    if (this.target.width === size) return;
    this.target.dispose();
    this.target = this.createTarget(size);
    this.paintUniform.value = this.target.texture;
    this.needsClear = true;
  }

  dispose(): void {
    this.target.dispose();
    this.stampMaterial.dispose();
    this.fadeMaterial.dispose();
  }
}
